/**
 * The claims this app reads off a Pivot-issued JWT. `exp` is the only one the
 * session clock depends on; `upn` is the subject. The index signature keeps
 * the other claims (`id`, `roles`, …) reachable without widening to `any`.
 */
export interface JwtPayload {
  readonly exp: number;
  readonly upn: string;
  readonly [claim: string]: unknown;
}

export class JwtHelper {
  private static urlBase64Decode(str: string): string {
    let output = str.replace(/-/g, "+").replace(/_/g, "/");
    switch (output.length % 4) {
      case 0:
        break;
      case 2:
        output += "==";
        break;
      case 3:
        output += "=";
        break;
      default:
        throw new Error("Illegal base64url string!");
    }
    return decodeURIComponent(
      ((window as unknown) as { escape: (value: string) => string }).escape(
        window.atob(output)
      )
    );
  }

  public decodeToken(token: string = ""): JwtPayload {
    if (token === null || token === "") {
      return { upn: "", exp: 0 };
    }
    const parts = token.split(".");
    if (parts.length !== 3) {
      throw new Error("JWT must have 3 parts");
    }
    const decoded = JwtHelper.urlBase64Decode(parts[1]);
    if (!decoded) {
      throw new Error("Cannot decode the token");
    }
    return JSON.parse(decoded) as JwtPayload;
  }

  public isTokenAlive(token: string = ""): boolean {
    if (token === null || token === "") {
      return false;
    }
    const decoded = this.decodeToken(token);
    const now = new Date().getTime() / 1000;
    return now < decoded.exp;
  }

  /**
   * Seconds remaining before `token` expires, per its own `exp` claim.
   * Negative once the token has expired, and `-1` when there is no token at
   * all — the caller must distinguish those, because neither is renewable but
   * only one means "there was a session here".
   *
   * TTL-agnostic by construction: it reads whatever `exp` the token actually
   * carries, so it keeps working unchanged whether the backend issues a
   * 48-hour token today or the ~1-hour token PPI-1848's backend half will
   * issue later. Nothing here may assume a particular token lifetime.
   */
  public getSecondsUntilExpiration(token: string = ""): number {
    if (token === null || token === "") {
      return -1;
    }
    const decoded = this.decodeToken(token);
    const now = new Date().getTime() / 1000;
    return Math.floor(decoded.exp - now);
  }
}
